<template>
  <v-app>
    <router-view v-if="!navbarHidden" name="navigation"></router-view>
    <v-main>
      <div class="main-ctn" :class="{ 'hide-nav': navbarHidden }">
        <router-multi-view class="wrapper" view-name="tab"></router-multi-view>
      </div>
    </v-main>
    <v-snackbar v-model="snackBar.show" bottom>
      {{ snackBar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" color="primary" text @click="dissmissSnackBar()">
          {{$t('dismiss')}}
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>

import { mapMutations, mapGetters } from 'vuex'
import amplitude from 'amplitude-js'
import Api from '@/api'
import { default as axios } from './utils/axios.instances' // eslint-disable-line

export default {
  name: 'App',
  computed: {
    ...mapGetters(['campaigns', 'selectedCampaign', 'snackBar', 'currentPlan', 'isRefreshingToken'])
  },
  components: {
  },
  data () {
    return {
      navbarHidden: false
    }
  },
  methods: {
    ...mapMutations({
      setSelectedCampaign: 'SET_SELECTED_CAMPAIGN',
      dissmissSnackBar: 'DISSMISS_SNACK_BAR'
    }),

    initAmplitude (user) {
      const identity = new amplitude.Identify().set('plan', this.currentPlan.id).set('email', user.email)
      amplitude.getInstance().setUserId(user.id)
      amplitude.getInstance().identify(identity)
    },

    handleMessage (event) {
      console.log('[qr-manager] msg from parent', event)
      if (event.data && event.data.type === 'token' && event.data.token) {
        this.$store.dispatch('setScantrustAuthToken', event.data.token)
        this.forwardTokenToIframes(event.data.token)
      } else if (event.data && event.data.type === 'tokenUpdateCompleted' && event.data.token) {
        this.$store.dispatch('setScantrustAuthToken', event.data.token)
        this.forwardTokenToIframes(event.data.token)
        if (this.isRefreshingToken) {
          this.$store.dispatch('processPendingRequest', event.data.token)
        }
      } else if (event.data && event.data.type === 'editor.beforeNavigate') {
        // Forward message from enterprise portal to e-label editor, asking if it's safe to navigate away, then pass response back up to enterprise portal. This handles the cases where the user is trying to navigate away from QR manager (via the hamburger menu) while having unsaved changes in e-label editor.
        const editorFrame = document.getElementById('elabel-editor-frame')
        // Only prompt through the editor when it's actually the visible view. QR manager keeps
        // inactive tabs mounted-but-hidden (router-multi-view), so a dirty editor left in the
        // background is still in the DOM. Forwarding beforeNavigate to a hidden editor pops a
        // modal the user can't see, and its leavePrompted deadlocks the portal's navigation.
        // If the editor isn't visible there's nothing to confirm - tell the portal it's safe to leave.
        const editorRect = editorFrame && editorFrame.getBoundingClientRect()
        const editorVisible = !!editorFrame &&
          editorFrame.offsetParent !== null &&
          editorRect.width > 0 && editorRect.height > 0 &&
          window.getComputedStyle(editorFrame).visibility !== 'hidden'
        if (!editorVisible) {
          window.parent.postMessage({ type: 'editor.readyToLeave' }, '*')
          return
        }

        const timeout = setTimeout(() => {
          window.removeEventListener('message', onResponse)
          window.parent.postMessage({ type: 'editor.readyToLeave' }, '*')
        }, 4000)

        const onResponse = (responseEvt) => {
          if (responseEvt.data.type === 'editor.leavePrompted') {
            // Editor is showing its unsaved-changes modal; cancel the fallback, forward the signal up, and wait for the user's decision
            clearTimeout(timeout)
            window.parent.postMessage({ type: 'editor.leavePrompted' }, '*')
            return
          }
          if (responseEvt.data.type === 'editor.readyToLeave' || responseEvt.data.type === 'editor.cancelLeave') {
            window.removeEventListener('message', onResponse)
            clearTimeout(timeout)
            window.parent.postMessage({ type: responseEvt.data.type }, '*')
          }
        }

        window.addEventListener('message', onResponse)
        editorFrame.contentWindow.postMessage({ type: 'editor.beforeNavigate' }, '*')
      }

      // Events from elabel landing pages editor
      if (event.data.type === 'forceTokenRefresh') {
        console.log('forward to portal')
        // Forward to portal
        window.parent.postMessage({ type: 'forceTokenRefresh' }, '*')
      }

      if (event.data.type === 'tokenUpdateRequested' && this.isFromEditorIframe(event)) {
        window.parent.postMessage({ type: 'tokenUpdateRequested' }, '*')
      }
    },

    editorIframes () {
      return ['elabel-editor-frame']
        .map((id) => document.getElementById(id))
        .filter((iframe) => iframe && iframe.contentWindow)
    },

    isFromEditorIframe (event) {
      return this.editorIframes().some((iframe) => iframe.contentWindow === event.source)
    },

    forwardTokenToIframes (token) {
      this.editorIframes().forEach((iframe) => {
        iframe.contentWindow.postMessage({ type: 'token', token: token }, '*')
        iframe.contentWindow.postMessage({ type: 'tokenUpdateCompleted', token: token }, '*')
      })
    }
  },
  async created () {
    if (Api.amplitude_key) {
      amplitude.getInstance().init(Api.amplitude_key)
      amplitude.getInstance().options.apiEndpoint = 'api2.amplitude.com'
    }
    if (this.$route.query.hidenav) {
      this.navbarHidden = true
    }
    const token = this.$route.query.token || Api.uat_token
    if (token) {
      this.$store.dispatch('setScantrustAuthToken', token)
      const user = await this.$store.dispatch('loadUserProfile')
      await this.$store.dispatch('loadQuotas')
      this.$i18n.locale = user.language
      this.initAmplitude(user)
      await this.$store.dispatch('loadCampaigns')
      if (!Object.keys(this.selectedCampaign).length) {
        this.setSelectedCampaign(this.campaigns[0])
      }
    }

    window.addEventListener('message', this.handleMessage, false)
    axios.initAxiosInterceptor(this.$store)
  },
  beforeDestroy () {
    // clean up listener
    window.removeEventListener('message', this.handleMessage)
  }
}
</script>

<style lang="sass">

@import '@/variables.sass'

main
  background: $light-bg

.main-ctn
  margin-left: auto
  margin-right: auto
  margin-top: 63px
  height: calc(100% - 63px)
  width: 100%
  background: $light-bg
  &.hide-nav
    margin-top: 0px

.wrapper
  height: 100%
  position: relative

.gs1-help-menu
  background: white
  padding: 16px
  color: #5D5D5D
  font-family: Roboto
  font-size: 16px
  font-weight: 400
  line-height: 24px
  letter-spacing: 0.25px
  a
    color: $primary
    text-decoration: none

</style>
